/**
 * Загрузка данных экрана. Целиком, расширять без нужды нельзя (05-ui.md §1.3).
 *
 * Библиотеку данных (TanStack Query) не берём осознанно: у нас девять маршрутов чтения и
 * ноль требований к кэшу между экранами. Зато нужны две вещи, которые в наивной реализации
 * ломаются молча:
 *   - гонка ответов при быстрой смене выбранного черновика — гасится AbortController, ОДНИМ
 *     на хук: отменяется и запрос из refetch(), а не только из эффекта;
 *   - обновление после каждой мутации — refetch() вызывается явно вызывающим, а не по таймеру.
 *
 * path === null значит «запрос не делать» — так выражаются зависимые запросы (версия
 * черновика неизвестна, пока не пришёл сам черновик).
 *
 * Загрузка вынесена в useCallback, а не запускается счётчиком в зависимостях эффекта:
 * счётчик, от которого эффект не зависит по существу, — это ровно то, что правило
 * useExhaustiveDependencies и считает ошибкой, и оно право. Здесь refetch() и эффект
 * вызывают ОДНУ функцию, поэтому расходиться им негде.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { get } from './client.ts';

export interface Resource<T> {
  readonly data: T | undefined;
  readonly error: Error | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
}

export function useResource<T>(path: string | null): Resource<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(path !== null);
  /**
   * Живой запрос ровно один. Раньше отменял только эффект — своё локальное соединение, — а
   * запрос из refetch() не отменял никто: после мутации на экране черновика идут два
   * refetch(), и их ответы могли приехать уже после того, как человек переключился на другой
   * черновик, и записать в состояние ЧУЖИЕ данные (F-088).
   */
  const acRef = useRef<AbortController | null>(null);

  const load = useCallback(
    (fresh: boolean): void => {
      acRef.current?.abort();
      acRef.current = null;
      if (path === null) {
        setData(undefined);
        setError(undefined);
        setLoading(false);
        return;
      }
      // `fresh` — это смена АДРЕСА: показанное принадлежит другому ресурсу, и оставлять его
      // на экране нельзя (F-087). Обычный refetch того же адреса состояние не сбрасывает:
      // мигать после каждой мутации экран не обязан.
      if (fresh) {
        setData(undefined);
        setError(undefined);
      }
      const ac = new AbortController();
      acRef.current = ac;
      setLoading(true);
      get<T>(path, ac.signal)
        .then((v) => {
          if (ac.signal.aborted) return;
          setData(v);
          setError(undefined);
        })
        .catch((e: unknown) => {
          if (ac.signal.aborted) return;
          setData(undefined);
          setError(e instanceof Error ? e : new Error(String(e)));
        })
        .finally(() => {
          if (!ac.signal.aborted) setLoading(false);
        });
    },
    [path],
  );

  useEffect(() => {
    load(true);
    return () => {
      acRef.current?.abort();
      acRef.current = null;
    };
  }, [load]);

  const refetch = useCallback(() => {
    load(false);
  }, [load]);

  return { data, error, loading, refetch };
}
